/**
 * UserDropdown Component
 * User avatar with dropdown menu for profile actions
 * Phase 1: UI Architecture Restructure
 * Phase 4: Polish - Keyboard navigation, animations, accessibility
 *
 * Design tokens used (from data.yaml):
 * - Animation: 200ms (normal) for dropdown fade
 * - Touch targets: min 44px for mobile
 * - Shadows: shadow-lg for dropdowns
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, KeyRound, LogOut, ChevronDown } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useAuth } from '@/hooks/useAuth'
import { useAnimatedOverlay } from './useAnimatedOverlay'

// UI copy
const translations = {
  profile: 'Profile',
  changePassword: 'Change Password',
  logout: 'Logout',
  loggingOut: 'Logging out...',
  userMenu: 'User menu',
}

// Menu items for keyboard navigation
const menuItems = [
  { id: 'change-password', action: 'changePassword' },
  { id: 'logout', action: 'logout' },
] as const

export function UserDropdown() {
  const navigate = useNavigate()
  const { logout, isLoggingOut } = useAuth()
  const user = useAuthStore((state) => state.user)
  const [open, setOpen] = useState(false)
  const { mounted: isOpen, animateIn: isAnimating } = useAnimatedOverlay(open, 200)
  const [, setFocusedIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuItemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const prevMounted = useRef(false)

  // Reset focus index on open (was inside openDropdown).
  useEffect(() => {
    if (open) setFocusedIndex(-1)
  }, [open])

  // Reset focus index + restore trigger focus when the dropdown finishes
  // closing and leaves the DOM (was inside closeDropdown's setTimeout).
  useEffect(() => {
    if (prevMounted.current && !isOpen) {
      setFocusedIndex(-1)
      triggerRef.current?.focus()
    }
    prevMounted.current = isOpen
  }, [isOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Keyboard navigation handler
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isOpen) {
        // Open dropdown on arrow down or enter
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          setOpen(true)
          // Focus first item after animation
          setTimeout(() => {
            setFocusedIndex(0)
            menuItemRefs.current[0]?.focus()
          }, 50)
        }
        return
      }

      switch (event.key) {
        case 'Escape':
          event.preventDefault()
          setOpen(false)
          break
        case 'ArrowDown':
          event.preventDefault()
          setFocusedIndex((prev) => {
            const next = prev < menuItems.length - 1 ? prev + 1 : 0
            menuItemRefs.current[next]?.focus()
            return next
          })
          break
        case 'ArrowUp':
          event.preventDefault()
          setFocusedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : menuItems.length - 1
            menuItemRefs.current[next]?.focus()
            return next
          })
          break
        case 'Tab':
          // Close on tab out
          setOpen(false)
          break
        case 'Home':
          event.preventDefault()
          setFocusedIndex(0)
          menuItemRefs.current[0]?.focus()
          break
        case 'End':
          event.preventDefault()
          setFocusedIndex(menuItems.length - 1)
          menuItemRefs.current[menuItems.length - 1]?.focus()
          break
      }
    },
    [isOpen]
  )

  const handleLogout = () => {
    setOpen(false)
    logout()
  }

  const handleChangePassword = () => {
    setOpen(false)
    navigate('/change-password')
  }

  // Get user initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const toggleDropdown = () => {
    // Direction keyed on mounted state (matches pre-refactor behavior — a
    // toggle click during the exit-animation window is a no-op close, not
    // a re-open).
    if (isOpen) {
      setOpen(false)
    } else {
      setOpen(true)
    }
  }

  return (
    <div className="relative" ref={dropdownRef} onKeyDown={handleKeyDown}>
      {/* Trigger Button - 44px touch target.
          Raw gray-300/white palette (not semantic tokens) is intentional: this
          trigger lives on the dark top navbar, so a white overlay/ring is
          correct in both themes; bg-foreground/10 would render dark in light
          mode and disappear on the dark navbar. */}
      <button
        ref={triggerRef}
        type="button"
        id="user-menu-button"
        onClick={toggleDropdown}
        className="
          flex items-center gap-2 rounded-lg px-2 py-1.5 min-h-[44px]
          text-sm text-gray-300 /* categorical: muted text on dark navbar */ hover:bg-white/10 hover:text-white
          transition-colors duration-150
          focus:outline-none focus:ring-2 focus:ring-white/50
        "
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls="user-dropdown-menu"
        aria-label={translations.userMenu}
      >
        {/* Avatar */}
        <div
          className="flex shrink-0 h-8 w-8 items-center justify-center rounded-full bg-primary text-white text-sm font-medium"
          aria-hidden="true"
        >
          {user?.name ? getInitials(user.name) : <User className="h-4 w-4" />}
        </div>

        {/* User name (hidden on small screens) */}
        <span className="hidden sm:block max-w-[120px] truncate">
          {user?.name || 'User'}
        </span>

        {/* Chevron */}
        <ChevronDown
          className={`
            h-4 w-4 transition-transform duration-200
            motion-reduce:transition-none
            ${isOpen ? 'rotate-180' : ''}
          `}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown Menu with animation */}
      {isOpen && (
        <div
          id="user-dropdown-menu"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="user-menu-button"
          className={`
            absolute right-0 top-full mt-2 w-64 rounded-lg border-2 border-border-subtle
            bg-background py-2 shadow-lg z-50
            transition-[opacity,transform] duration-200 ease-out
            motion-reduce:transition-none motion-reduce:transform-none
            ${isAnimating
              ? 'opacity-100 scale-100 translate-y-0'
              : 'opacity-0 scale-95 -translate-y-2'
            }
          `}
        >
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-border-subtle">
            <p className="text-sm font-medium text-foreground truncate">
              {user?.name || 'User'}
            </p>
            <p className="text-xs text-foreground-muted truncate">
              {user?.email || 'user@example.com'}
            </p>
          </div>

          {/* Menu Items */}
          <div className="py-1" role="none">
            {/* Change Password */}
            <button
              ref={(el) => { menuItemRefs.current[0] = el }}
              type="button"
              role="menuitem"
              onClick={handleChangePassword}
              className="
                flex w-full items-center gap-3 px-4 py-3 min-h-[44px]
                text-sm text-foreground hover:bg-background-subtle
                transition-colors duration-150
                focus:outline-none focus:bg-background-muted
              "
            >
              <KeyRound className="h-4 w-4 text-foreground-muted" aria-hidden="true" />
              {translations.changePassword}
            </button>

            <div className="my-1 border-t border-border-subtle" role="separator" />

            {/* Logout */}
            <button
              ref={(el) => { menuItemRefs.current[1] = el }}
              type="button"
              role="menuitem"
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="
                flex w-full items-center gap-3 px-4 py-3 min-h-[44px]
                text-sm text-error hover:bg-error-bg
                transition-colors duration-150 disabled:opacity-50
                focus:outline-none focus:bg-error-bg
              "
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              {isLoggingOut ? translations.loggingOut : translations.logout}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserDropdown
